import { CheckCircle as CheckIcon, Cancel as ErrorIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Collapse,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';
import type React from 'react';
import {
  getPasswordRequirementsText,
  getPasswordStrengthLabel,
  validatePassword,
} from '../../utils/passwordValidation';

interface PasswordStrengthIndicatorProps {
  password: string;
  userInfo?: {
    email?: string;
    displayName?: string;
    clientName?: string;
  };
  showRequirements?: boolean;
  showStrengthMeter?: boolean;
  showValidationMessages?: boolean;
}

type RequirementStatus = 'valid' | 'invalid' | 'pending';

const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({
  password,
  userInfo = {},
  showRequirements = true,
  showStrengthMeter = true,
  showValidationMessages = true,
}) => {
  const theme = useTheme();
  const validation = validatePassword(password, userInfo);
  const strengthInfo = getPasswordStrengthLabel(validation.strength);
  const requirements = getPasswordRequirementsText();

  const getRequirementStatus = (requirement: string, password: string): RequirementStatus => {
    if (!password) {
      return 'pending';
    }

    if (requirement.includes('8 characters')) {
      return password.length >= 8 ? 'valid' : 'invalid';
    }
    if (requirement.includes('uppercase')) {
      return /[A-Z]/.test(password) ? 'valid' : 'invalid';
    }
    if (requirement.includes('lowercase')) {
      return /[a-z]/.test(password) ? 'valid' : 'invalid';
    }
    if (requirement.includes('number')) {
      return /\d/.test(password) ? 'valid' : 'invalid';
    }
    if (requirement.includes('special character')) {
      return /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`]/.test(password) ? 'valid' : 'invalid';
    }
    if (requirement.includes('common password')) {
      const commonPasswords = [
        'password',
        'password123',
        '123456',
        '123456789',
        'qwerty',
        'abc123',
      ];
      return !commonPasswords.includes(password.toLowerCase()) ? 'valid' : 'invalid';
    }
    if (requirement.includes('personal information')) {
      const hasPersonalInfoError = validation.errors.some(
        (error) => error.includes('email') || error.includes('name') || error.includes('company')
      );
      return hasPersonalInfoError ? 'invalid' : 'valid';
    }

    return 'pending';
  };

  const getStatusIcon = (status: RequirementStatus): React.ReactNode => {
    switch (status) {
      case 'valid':
        return <CheckIcon sx={{ color: theme.palette.success.main, fontSize: 16 }} />;
      case 'invalid':
        return <ErrorIcon sx={{ color: theme.palette.error.main, fontSize: 16 }} />;
      default:
        return (
          <Box
            sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: theme.palette.greyLight }}
          />
        );
    }
  };

  return (
    <Box sx={{ mt: 1 }}>
      {/* Password Strength Meter */}
      {showStrengthMeter && password && (
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}
          >
            <Typography variant="body2" color="text.primary">
              Password Strength
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: strengthInfo.color,
                fontWeight: 'bold',
                fontSize: '0.75rem',
              }}
            >
              {strengthInfo.label}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={validation.strength}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: theme.palette.background.default,
              '& .MuiLinearProgress-bar': {
                backgroundColor: strengthInfo.color,
                borderRadius: 3,
              },
            }}
          />
        </Box>
      )}

      {/* Validation Messages */}
      {showValidationMessages &&
        (validation.errors.length > 0 || validation.warnings.length > 0) && (
          <Box sx={{ mb: 2 }}>
            {validation.errors.map((error) => (
              <Alert
                key={`error-${error}`}
                severity="error"
                sx={{ mb: 1, py: 0.5, fontSize: '0.75rem' }}
              >
                {error}
              </Alert>
            ))}
            {validation.warnings.map((warning) => (
              <Alert
                key={`warning-${warning}`}
                severity="warning"
                sx={{ mb: 1, py: 0.5, fontSize: '0.75rem' }}
              >
                {warning}
              </Alert>
            ))}
          </Box>
        )}

      {/* Password Requirements Checklist */}
      {showRequirements && (
        <Collapse in={!!password || showRequirements}>
          <Box
            sx={{
              bgcolor: 'transparent',
              borderRadius: 1,
              p: 2,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ fontWeight: 'bold', fontSize: '0.65rem' }}
            >
              Password Requirements
            </Typography>
            <List dense sx={{ py: 0 }}>
              {requirements.map((requirement) => {
                const status = getRequirementStatus(requirement, password);
                return (
                  <ListItem key={requirement} sx={{ py: 0.125, px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 20 }}>{getStatusIcon(status)}</ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          sx={{
                            fontSize: '0.75rem',
                            color:
                              status === 'valid'
                                ? theme.palette.text.primary
                                : status === 'invalid'
                                  ? theme.palette.bidlogiq.brick
                                  : theme.palette.text.primary,
                          }}
                        >
                          {requirement}
                        </Typography>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

export default PasswordStrengthIndicator;
